import React from "react";
import { motion } from "framer-motion";
import { FaWhatsapp, FaStripe } from "react-icons/fa";
import { Star, BadgeCheck, BellRing, Wallet, ShieldCheck } from "lucide-react";

export default function Features() {
  return (
    <section
      id="features"
      data-testid="features-bento-grid"
      className="bg-white"
    >
      <div className="px-6 md:px-12 lg:px-24 py-20 md:py-32">
        <div className="max-w-2xl">
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
            The toolkit
          </p>
          <h2 className="font-display mt-3 text-4xl sm:text-5xl tracking-tighter font-black text-[#0A0A0A] leading-[1]">
            Everything a removals platform
            <br />
            should already have.
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-4 md:gap-5">
          {/* Stripe Escrow - large */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            data-testid="feature-stripe-escrow"
            className="md:col-span-5 lg:col-span-7 border border-[#0A0A0A]/10 bg-gradient-to-br from-[#0A0A0A] to-[#1a1a1a] text-white p-8 md:p-10 relative overflow-hidden min-h-[320px]"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-white/60">
              <FaStripe size={36} className="text-white" />
              <span>Powered by Stripe Connect</span>
            </div>
            <h3 className="font-display mt-8 text-3xl md:text-4xl font-black tracking-tight leading-[1.05]">
              Charge → hold → capture
              <br />
              <span className="text-[#5BB247]">on completion.</span>
            </h3>
            <p className="mt-4 max-w-md text-white/70 leading-relaxed">
              Real escrow on every job. Customers never overpay; drivers
              never get ghosted. Disputes resolved with audit logs &amp; proof
              photos.
            </p>

            {/* Mock UI strip */}
            <div className="absolute -right-10 -bottom-10 md:right-6 md:bottom-6 w-[260px] border border-white/15 bg-white/5 backdrop-blur p-4 font-mono text-xs">
              <div className="flex items-center justify-between text-white/50">
                <span>txn_5x9a</span>
                <span className="text-[#5BB247]">HELD</span>
              </div>
              <div className="mt-2 text-2xl text-white font-display tracking-tight">
                £485.00
              </div>
              <div className="mt-3 grid grid-cols-3 gap-1">
                <div className="h-1.5 bg-[#5BB247]" />
                <div className="h-1.5 bg-[#5BB247]" />
                <div className="h-1.5 bg-white/15" />
              </div>
              <p className="mt-2 text-[10px] uppercase tracking-wider text-white/50">
                Awaiting proof of completion
              </p>
            </div>
          </motion.div>

          {/* WhatsApp - medium */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.08, ease: "easeOut" }}
            data-testid="feature-whatsapp"
            className="md:col-span-3 lg:col-span-5 border border-[#0A0A0A]/10 bg-[#5BB247] text-white p-8 md:p-10 min-h-[320px] flex flex-col justify-between"
          >
            <div>
              <FaWhatsapp size={40} className="text-white" />
              <h3 className="font-display mt-6 text-3xl md:text-4xl font-black tracking-tight leading-[1.05]">
                Runs on WhatsApp.
              </h3>
              <p className="mt-3 text-white/80 leading-relaxed">
                Drivers accept jobs, upload proofs, and get paid — all without
                downloading an app.
              </p>
            </div>
            <div className="mt-6 space-y-2">
              <div className="bg-white/15 backdrop-blur border border-white/15 p-3 text-sm">
                <span className="font-semibold">Raanah Bot:</span> New job in
                Leeds · 3-bed removal · £485. Accept?
              </div>
              <div className="bg-white text-[#0A0A0A] border border-white p-3 text-sm self-end ml-8">
                ✅ Accept
              </div>
            </div>
          </motion.div>

          {/* Verification */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.12, ease: "easeOut" }}
            data-testid="feature-verification"
            className="md:col-span-4 lg:col-span-4 border border-[#0A0A0A]/10 bg-white p-8 hover:bg-[#F8FAFC] transition-colors"
          >
            <BadgeCheck size={28} className="text-[#1E4D55]" />
            <h3 className="font-display mt-5 text-2xl font-bold tracking-tight text-[#0A0A0A]">
              DVLA &amp; vehicle verified
            </h3>
            <p className="mt-2 text-[#475569] leading-relaxed text-sm">
              Upload licence, V5C, insurance &amp; PHV (if applicable). Admin
              verifies before first job. No grey area.
            </p>
          </motion.div>

          {/* Weekly Payouts */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.16, ease: "easeOut" }}
            data-testid="feature-payouts"
            className="md:col-span-4 lg:col-span-4 border border-[#0A0A0A]/10 bg-[#1E4D55] text-white p-8"
          >
            <Wallet size={28} className="text-white" />
            <h3 className="font-display mt-5 text-2xl font-bold tracking-tight">
              Weekly payouts
            </h3>
            <p className="mt-2 text-white/80 leading-relaxed text-sm">
              Commission engine settles every Friday. Drivers see earnings live,
              get paid to bank without invoicing.
            </p>
          </motion.div>

          {/* Ratings */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            data-testid="feature-ratings"
            className="md:col-span-4 lg:col-span-4 border border-[#0A0A0A]/10 bg-white p-8"
          >
            <Star size={28} className="text-[#F5A623] fill-[#F5A623]" />
            <h3 className="font-display mt-5 text-2xl font-bold tracking-tight text-[#0A0A0A]">
              Two-way ratings
            </h3>
            <p className="mt-2 text-[#475569] leading-relaxed text-sm">
              Customers and drivers rate after every job. Bad actors filter out
              of the pool. Trust compounds.
            </p>
          </motion.div>

          {/* Doc reminders */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.24, ease: "easeOut" }}
            data-testid="feature-reminders"
            className="md:col-span-4 lg:col-span-6 border border-[#0A0A0A]/10 bg-white p-8"
          >
            <BellRing size={28} className="text-[#1E4D55]" />
            <h3 className="font-display mt-5 text-2xl font-bold tracking-tight text-[#0A0A0A]">
              Document expiry reminders
            </h3>
            <p className="mt-2 text-[#475569] leading-relaxed text-sm">
              Auto-pings drivers 30/14/3 days before MOT, V5C, insurance &amp;
              PHV expiry. No compliance gaps mid-job.
            </p>
          </motion.div>

          {/* Admin Dashboard */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.28, ease: "easeOut" }}
            data-testid="feature-admin"
            className="md:col-span-4 lg:col-span-6 border border-[#0A0A0A]/10 bg-[#0A0A0A] text-white p-8"
          >
            <ShieldCheck size={28} className="text-[#5BB247]" />
            <h3 className="font-display mt-5 text-2xl font-bold tracking-tight">
              Admin oversight
            </h3>
            <p className="mt-2 text-white/70 leading-relaxed text-sm">
              Verify docs, mediate disputes, audit every escrow event from one
              clean console.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
